import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

// component imports
import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import {
    RoundListNavItem,
    SeasonNavItem,
    CompNavItem
} from 'common/react/components/competition-nav/nav-items';

//store imports
import { navSelector } from 'store/modules/competition-nav/selectors/nav-selector';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useNavSync } from 'common/react/components/competition-nav/hooks/use-nav-sync';
import { useWindowSizeV2 } from 'common/react/hooks/use-window-resize';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { SettingsButton } from 'common/react/components/SettingsButton';
import { FilterButton } from 'common/react/components/FilterButton';
import { UPDATE_NAV_ITEM } from 'common/store/modules/competition-nav/actions';
import { getTranslation } from 'common/react/utils/translations';

export const VideoHub = ({ competitionId, seasonId, title }) => {
    const dispatch = useDispatch();
    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);
    const [resetFilterBtnStyles, setResetFilterBtnStyles] = useState(false);
    const windowSize = useWindowSizeV2();

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
    }, [windowSize.width]);

    const initialValues = { competitionId, seasonId };
    const competitionNavRef = useRef();

    const nav = useSelector(navSelector);
    const options = useCompetitionNavigationOptions(nav);
    useNavSync({ ...options, initialValues });

    const competitions = options.competitions;

    const seasons = useSelector(
        (state) =>
            state.seasons[`competition_${nav.competition.id}`]?.list ?? []
    );

    const hasChangedFromDefaults = () => {
        if (!nav?.competition || !nav?.season || !nav?.round) {
            return false;
        }
        const isCompetitionChanged =
            nav?.competition?.id !== nav?.competition?.initialItemId;
        const isSeasonChanged =
            parseInt(nav?.season?.id, 10) !==
            parseInt(nav?.season?.initialItemId, 10);

        return isCompetitionChanged || isSeasonChanged;
    };

    const resetToDefaults = () => {
        const initialCompetitionId = nav?.competition?.initialItemId || '';
        const initialSeasonId = nav?.season?.initialItemId || '';

        if (nav?.competition?.id !== initialCompetitionId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'competition',
                    value: { id: initialCompetitionId }
                })
            );
        }
        if (nav?.season?.id !== initialSeasonId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'season',
                    value: { id: initialSeasonId }
                })
            );
        }
    };

    const hasSeasonChanged = useCallback(() => {
        return nav.season.id !== nav.season.initialItemId;
    }, [nav.season.id, nav.season.initialItemId]);

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: nav,
        options,
        filtersFields: ['competition', 'season', 'round'],
        filtersRef: competitionNavRef
    });

    return (
        <div
            className="competition-nav filter-dropdown rounded-roundlist-wrapper"
            ref={competitionNavRef}
        >
            <StatsSection.Main>
                <StatsSection.Heading title={title} />
                <StatsSection.Nav
                    showResponsiveItems={showResponsiveItems}
                    withScrollableFilter={!showResponsiveItems}
                >
                    <StatsSection.NavItems>
                        <CompNavItem
                            competitions={competitions}
                            classes="video-hub-nav__comp-item"
                        />
                        {!showResponsiveItems ? (
                            <SeasonNavItem seasons={seasons} hideLabel />
                        ) : null}
                        {!showResponsiveItems && hasChangedFromDefaults() ? (
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        ) : null}
                        {showResponsiveItems ? (
                            <FilterButton
                                classes="fixtures-nav__filter-button"
                                onChange={() =>
                                    setAreFilterOptionsVisible((prev) => !prev)
                                }
                                areFilterOptionsVisible={
                                    areFilterOptionsVisible
                                }
                                resetFilterBtnStyles={resetFilterBtnStyles}
                                setResetFilterBtnStyles={
                                    setResetFilterBtnStyles
                                }
                                showFilterLabel={false}
                                showActiveCircle
                                isFilterSelectedCustom={hasSeasonChanged}
                            />
                        ) : null}
                        {showResponsiveItems ? (
                            <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                                <SettingsButton />
                            </StatsSection.SettingsButton>
                        ) : null}
                    </StatsSection.NavItems>
                    <StatsSection.SettingsButton>
                        <SettingsButton />
                    </StatsSection.SettingsButton>
                    <StatsSection.RoundButtons>
                        <RoundListNavItem
                            classes={'competition-nav__round-filter-wrapper'}
                        />
                    </StatsSection.RoundButtons>
                    {showResponsiveItems && areFilterOptionsVisible ? (
                        <StatsSection.NavFilterItems>
                            <SeasonNavItem seasons={seasons} hideLabel />
                        </StatsSection.NavFilterItems>
                    ) : null}
                </StatsSection.Nav>
            </StatsSection.Main>
        </div>
    );
};

VideoHub.propTypes = {
    seasonPid: PropTypes.string,
    competitionId: PropTypes.string,
    seasonId: PropTypes.string,
    round: PropTypes.string,
    title: PropTypes.string
};
